import React, { useEffect, useState } from 'react';
import { taskService } from '../../../../services/tasks/taskService.js';

const initialValidationState = {
  duracion: '',
  valorHora: '',
  motivo: '',
  salidaGuardia: false,
  salidaFtthEmergencia: false
};

const requiredFieldLabels = {
  duracion: 'Duración',
  valorHora: 'Valor hora',
  motivo: 'Motivo'
};

const validationTypeLabel = {
  requested: 'Solicitada',
  validated: 'Validada'
};

const ValidacionTab = ({ taskId, task }) => {
  const [validationData, setValidationData] = useState(initialValidationState);
  const [touchedFields, setTouchedFields] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestState, setRequestState] = useState({ type: '', message: '' });
  const technicianValidationReason = task?.validateReason?.reasonTechnician;
  const [validationHistory, setValidationHistory] = useState(
    Array.isArray(task?.validateHistory) ? task.validateHistory : []
  );

  useEffect(() => {
    setValidationHistory(Array.isArray(task?.validateHistory) ? task.validateHistory : []);
  }, [task?.validateHistory]);

  useEffect(() => {
    if (!requestState.message) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      setRequestState({ type: '', message: '' });
    }, 5000);

    return () => clearTimeout(timerId);
  }, [requestState.message]);

  const formatDateTime = (value) => {
    if (!value) {
      return 'Sin fecha';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return 'Fecha inválida';
    }

    return new Intl.DateTimeFormat('es-AR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(parsedDate);
  };

  const formatValueHour = (value) => {
    if (!value) {
      return 'No informado';
    }

    const normalized = String(value).toLowerCase();
    if (normalized === 'normal') {
      return 'Normal';
    }
    if (normalized === 'extra') {
      return 'Extra';
    }
    if (normalized === 'feriado') {
      return 'Feriado';
    }

    return value;
  };

  const handleFieldChange = (field, value) => {
    setRequestState({ type: '', message: '' });
    setValidationData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFieldBlur = (field) => {
    setTouchedFields((prev) => ({
      ...prev,
      [field]: true
    }));
  };

  const handleDurationChange = (event) => {
    const { value } = event.target;
    if (value === '' || /^\d+$/.test(value)) {
      handleFieldChange('duracion', value);
    }
  };

  const getFieldError = (field) => {
    if (field === 'duracion') {
      if (!validationData.duracion.trim()) {
        return 'Este campo es obligatorio';
      }

      if (Number(validationData.duracion) < 0) {
        return 'Debe ser 0 o mayor';
      }

      return '';
    }

    if (field === 'valorHora') {
      return validationData.valorHora ? '' : 'Este campo es obligatorio';
    }

    if (field === 'motivo') {
      return validationData.motivo.trim() ? '' : 'Este campo es obligatorio';
    }

    return '';
  };

  const shouldShowError = (field) => {
    return (touchedFields[field] || submitAttempted) && Boolean(getFieldError(field));
  };

  const handleValidateTask = async () => {
    setSubmitAttempted(true);

    const hasErrors = Object.keys(requiredFieldLabels).some((field) => Boolean(getFieldError(field)));
    if (hasErrors) {
      return;
    }

    if (!taskId) {
      setRequestState({
        type: 'error',
        message: 'No se encontró el identificador de la tarea para validar.'
      });
      return;
    }

    setIsSubmitting(true);
    setRequestState({ type: '', message: '' });

    try {
      const payload = await taskService.validateTask(taskId, {
        estimatedDuration: Number(validationData.duracion),
        valueHour: validationData.valorHora,
        reasonAdmin: validationData.motivo.trim(),
        onDuty: validationData.salidaGuardia,
        isFTTH: validationData.salidaFtthEmergencia
      });

      if (Array.isArray(payload?.task?.validateHistory)) {
        setValidationHistory(payload.task.validateHistory);
      }

      setValidationData(initialValidationState);
      setTouchedFields({});
      setSubmitAttempted(false);

      setRequestState({
        type: 'success',
        message: payload?.message || 'La tarea fue validada correctamente.'
      });
    } catch (error) {
      setRequestState({
        type: 'error',
        message: error.message || 'Ocurrió un error al validar la tarea.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-info bg-info/12 p-6">
        <h4 className="text-sm font-semibold text-info">Solicitud de Validacion</h4>
        <p className="mt-1 text-xs uppercase tracking-wide text-info">Motivo del técnico</p>
        <p className="mt-1 text-sm text-info">
          {technicianValidationReason || 'No hay motivo cargado por el técnico.'}
        </p>
      </div>

      <div className="rounded-3xl border border-line bg-surface/70 p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-content">Validacion</h3>
          <p className="mt-1 text-sm text-content-muted">
            Completa la información de validación de la tarea.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="duracion" className="mb-2 block text-sm font-medium text-content">
              Duración <span className="text-red-400">*</span>
            </label>
            <div
              className={`grid grid-cols-4 overflow-hidden rounded-xl border bg-bg/80 transition-colors ${
                shouldShowError('duracion') ? 'border-red-500' : 'border-line'
              }`}
            >
              <input
                id="duracion"
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={validationData.duracion}
                onChange={handleDurationChange}
                onBlur={() => handleFieldBlur('duracion')}
                placeholder="Ingresa la duración"
                className="col-span-3 border-0 bg-transparent px-3 py-2 text-sm text-content outline-none"
              />
              <div className="col-span-1 flex items-center justify-center border-l border-line bg-surface/80 px-2 py-2 text-xs font-medium uppercase tracking-wide text-content sm:text-sm">
                Minutos
              </div>
            </div>
            {shouldShowError('duracion') && (
              <p className="mt-1 text-xs text-red-400">{getFieldError('duracion')}</p>
            )}
          </div>

          <div>
            <label htmlFor="valor-hora" className="mb-2 block text-sm font-medium text-content">
              Valor hora <span className="text-red-400">*</span>
            </label>
            <select
              id="valor-hora"
              value={validationData.valorHora}
              onChange={(event) => handleFieldChange('valorHora', event.target.value)}
              onBlur={() => handleFieldBlur('valorHora')}
              className={`w-full rounded-xl border bg-bg/80 px-3 py-2 text-sm text-content outline-none transition-colors focus:border-amber ${
                shouldShowError('valorHora') ? 'border-red-500' : 'border-line'
              }`}
            >
              <option value="">Selecciona un valor</option>
              <option value="Normal">Normal</option>
              <option value="extra">Extra</option>
              <option value="feriado">Feriado</option>
            </select>
            {shouldShowError('valorHora') && (
              <p className="mt-1 text-xs text-red-400">{getFieldError('valorHora')}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label htmlFor="motivo" className="mb-2 block text-sm font-medium text-content">
              Motivo <span className="text-red-400">*</span>
            </label>
            <textarea
              id="motivo"
              value={validationData.motivo}
              onChange={(event) => handleFieldChange('motivo', event.target.value)}
              onBlur={() => handleFieldBlur('motivo')}
              rows={4}
              placeholder="Describe el motivo"
              className={`w-full rounded-xl border bg-bg/80 px-3 py-2 text-sm text-content outline-none transition-colors focus:border-amber ${
                shouldShowError('motivo') ? 'border-red-500' : 'border-line'
              }`}
            />
            {shouldShowError('motivo') && (
              <p className="mt-1 text-xs text-red-400">{getFieldError('motivo')}</p>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <label
            htmlFor="salida-guardia"
            className="flex items-center justify-between rounded-2xl border border-line bg-bg/70 px-4 py-3"
          >
            <div>
              <p className="text-sm font-medium text-content">Salida de Guardia</p>
              <p className="text-xs text-content-muted">
                Estado: {validationData.salidaGuardia ? 'Activado' : 'Desactivado'}
              </p>
            </div>
            <input
              id="salida-guardia"
              type="checkbox"
              checked={validationData.salidaGuardia}
              onChange={(event) => handleFieldChange('salidaGuardia', event.target.checked)}
              className="h-5 w-5 cursor-pointer rounded border-line bg-surface text-amber focus:ring-amber"
            />
          </label>

          <label
            htmlFor="salida-ftth-emergencia"
            className="flex items-center justify-between rounded-2xl border border-line bg-bg/70 px-4 py-3"
          >
            <div>
              <p className="text-sm font-medium text-content">Trabajo FTTH</p>
              <p className="text-xs text-content-muted">
                Estado: {validationData.salidaFtthEmergencia ? 'Activado' : 'Desactivado'}
              </p>
            </div>
            <input
              id="salida-ftth-emergencia"
              type="checkbox"
              checked={validationData.salidaFtthEmergencia}
              onChange={(event) => handleFieldChange('salidaFtthEmergencia', event.target.checked)}
              className="h-5 w-5 cursor-pointer rounded border-line bg-surface text-amber focus:ring-amber"
            />
          </label>
        </div>

        {requestState.message && (
          <div
            className={`mt-6 rounded-xl border px-4 py-3 text-sm ${
              requestState.type === 'success'
                ? 'border-green-600 bg-green-900/20 text-green-300'
                : 'border-red-600 bg-red-900/20 text-red-300'
            }`}
          >
            {requestState.message}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleValidateTask}
            disabled={isSubmitting}
            className="rounded-xl bg-amber px-5 py-2.5 text-sm font-semibold text-on-amber transition-colors hover:bg-amber-hover disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? 'Validando...' : 'Validar'}
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-line bg-surface/70 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-content">Historial de Validacion</h3>
          <p className="mt-1 text-sm text-content-muted">
            Registro de solicitudes y validaciones realizadas sobre la tarea.
          </p>
        </div>

        {validationHistory.length === 0 ? (
          <p className="rounded-xl border border-line bg-bg/60 px-4 py-3 text-sm text-content">
            No hay historial de validacion disponible.
          </p>
        ) : (
          <div className="space-y-3">
            {validationHistory.map((item, index) => (
              <div
                key={`${item?.date || 'sin-fecha'}-${index}`}
                className="rounded-2xl border border-line bg-bg/70 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex rounded-full bg-info/12 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-info">
                    {validationTypeLabel[item?.type] || item?.type || 'Sin tipo'}
                  </span>
                  <span className="text-xs text-content-muted">{formatDateTime(item?.date)}</span>
                </div>

                <div className="mt-3 grid gap-2 text-sm text-content md:grid-cols-2">
                  <p>
                    <span className="text-content-muted">Usuario:</span> {item?.userName || 'No informado'}
                  </p>
                  <p>
                    <span className="text-content-muted">Email:</span> {item?.userEmail || 'No informado'}
                  </p>
                </div>

                {item?.reasonAdmin && (
                  <p className="mt-2 text-sm text-content">
                    <span className="text-content-muted">Motivo del administrador:</span> {item.reasonAdmin}
                  </p>
                )}

                {item?.type === 'validated' && (
                  <div className="mt-3 grid gap-2 text-sm text-content md:grid-cols-2">
                    <p>
                      <span className="text-content-muted">Valor hora:</span>{' '}
                      {formatValueHour(item?.valueHour)}
                    </p>
                    <p>
                      <span className="text-content-muted">Duracion:</span>{' '}
                      {item?.estimatedDuration ?? 'No informada'}
                    </p>
                    <p>
                      <span className="text-content-muted">Salida de Guardia:</span>{' '}
                      {item?.onDuty ? 'Si' : 'No'}
                    </p>
                    <p>
                      <span className="text-content-muted">Trabajo FTTH:</span>{' '}
                      {item?.isFTTH ? 'Si' : 'No'}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidacionTab;
